// external
import { nanoid } from 'nanoid';
import { useCallback, useEffect, useRef, useState } from 'react';

// types
import type { DealerDiscount, DiscountOption } from './discountTypes';

// validation
import { validateDiscountName, validateDiscountAmount } from './validation';

const createDiscount = (): DealerDiscount => ({
    id: nanoid(),
    discountName: '',
    discountAmount: '',
    selected: false
});

export const useDealerDiscounts = (discountValues: DiscountOption[], initialDiscounts?: DealerDiscount[]) => {
    const debounceRef = useRef<Record<string, NodeJS.Timeout>>({});
    const [discounts, setDiscounts] = useState<DealerDiscount[]>(() =>
        initialDiscounts && initialDiscounts.length ? initialDiscounts : [createDiscount()]
    );

    const getAvailableOptions = useCallback(
        () =>
            discountValues.map((option, index) => ({
                ...option,
                // we create a unique value while preserving original data
                // we are doing this because of how interstate ComboBox works
                value: `${option.label}_${option.value}_${index}`
            })),
        [discountValues]
    );

    const getSelectedValue = useCallback(
        (discountName: string, discountAmount: string): DiscountOption[] => {
            if (!discountName) return [];
            const matchingOption = discountValues.find(
                (option) => option.label === discountName && option.value === discountAmount
            );
            if (matchingOption) {
                const index = discountValues.findIndex(
                    (opt) => opt.label === matchingOption.label && opt.value === matchingOption.value
                );
                const uniqueValue = `${matchingOption.label}_${matchingOption.value}_${index}`;
                return [{ ...matchingOption, value: uniqueValue }];
            }
            return [{ label: discountName, value: discountAmount }];
        },
        [discountValues]
    );

    const updateDiscount = useCallback((id: string, updates: Partial<DealerDiscount>) => {
        setDiscounts((previousDiscounts) =>
            previousDiscounts.map((currentDiscount) =>
                currentDiscount.id === id ? { ...currentDiscount, ...updates } : currentDiscount
            )
        );
    }, []);

    const addDiscount = useCallback(() => setDiscounts((previousDiscounts) => [...previousDiscounts, createDiscount()]), []);

    const removeDiscount = useCallback(
        (id: string) =>
            setDiscounts((previousDiscounts) => previousDiscounts.filter((currentDiscount) => currentDiscount.id !== id)),
        []
    );

    const updateDiscountName = useCallback((id: string, selectedOption: DiscountOption | null | undefined) => {
        setDiscounts((previousDiscounts) =>
            previousDiscounts.map((discount) => {
                if (discount.id !== id) {
                    return discount;
                }

                const newName = selectedOption?.label || '';
                const updatedDiscount = { ...discount, discountName: newName };

                if (selectedOption && selectedOption.value && selectedOption.value !== selectedOption.label) {
                    const valueToSplit = Array.isArray(selectedOption.value)
                        ? selectedOption.value[0]?.value
                        : selectedOption.value;
                    if (typeof valueToSplit === 'string') {
                        const parts = valueToSplit.split('_');
                        if (parts.length >= 2) {
                            updatedDiscount.discountAmount = parts[1];
                        }
                    }
                }
                const { error: nameError } = validateDiscountName({
                    name: newName,
                    allDiscounts: previousDiscounts,
                    currentDiscountId: id
                });
                updatedDiscount.nameError = nameError;

                if (selectedOption?.value) {
                    const { error: amountError } = validateDiscountAmount(updatedDiscount.discountAmount);
                    updatedDiscount.amountError = amountError;
                }

                return updatedDiscount;
            })
        );
    }, []);

    const updateDiscountAmount = useCallback((id: string, value: string) => {
        let targetDiscount: DealerDiscount | undefined;
        let nameError: string | undefined;
        const { error: amountError } = validateDiscountAmount(value);

        setDiscounts((previousDiscounts) => {
            const updatedDiscounts = previousDiscounts.map((currentDiscount) => {
                if (currentDiscount.id !== id) {
                    return currentDiscount;
                }

                targetDiscount = currentDiscount;
                const hasValue = value != null && value !== '';

                if (hasValue) {
                    const { error } = validateDiscountName({
                        name: currentDiscount.discountName || '',
                        allDiscounts: previousDiscounts,
                        currentDiscountId: id
                    });
                    nameError = error;
                }

                const discountUpdates = {
                    discountAmount: value,
                    amountError,
                    ...(nameError && { nameError })
                };

                return { ...currentDiscount, ...discountUpdates };
            });

            return targetDiscount ? updatedDiscounts : previousDiscounts;
        });
    }, []);

    const setCustomDiscountName = useCallback(
        (id: string, name: string) => {
            if (!name) return;
            updateDiscount(id, { discountName: name, selected: true });
        },
        [updateDiscount]
    );

    const clearDiscount = useCallback((id: string) => {
        setDiscounts((previous) =>
            previous.map((d) =>
                d.id === id
                    ? {
                          ...d,
                          discountName: '',
                          discountAmount: '',
                          nameError: undefined,
                          amountError: undefined,
                          selected: false
                      }
                    : d
            )
        );
    }, []);

    useEffect(() => () => Object.values(debounceRef.current).forEach(clearTimeout), []);

    return {
        discounts,
        getAvailableOptions,
        getSelectedValue,
        addDiscount,
        removeDiscount,
        updateDiscountName,
        updateDiscountAmount,
        setCustomDiscountName,
        updateDiscount,
        clearDiscount
    };
};
